<template>
  <div class="mobile-nav-content" :class="eventClass">
    <div v-if="mounted && isMobile" class="top-content" :class="{ 'top-content--white-bg': activeIdsMap['accountContent'] }">
      <MobileNavTopBar />
    </div>
    <div class="body-content">
      <BetterTransitionGroup>
        <NavBackButton
          v-if="mounted && activeCategoryData"
          :data="activeCategoryData"
          :class="{ hide: activeIdsMap['accountContent'] }"
          class="transition-panel"
        />
        <div v-show="isRootMode" class="transition-panel l1-flyout">
          <RootCategories @on-select="showSubCategories" />
        </div>
        <template v-if="mounted">
          <MobileStoreInfo v-if="isDomestic && activeIdsMap['store']" class="transition-panel" :class="flyoutClasses" />
          <RegistryInfo v-show="activeIdsMap['registry']" class="transition-panel" :class="flyoutClasses" />
          <template v-if="isMobile">
            <AccountContent v-show="activeIdsMap['accountContent']" class="transition-panel" :class="flyoutClasses" />
          </template>
        </template>
        <template v-if="isNavSSR">
          <template v-for="flyout in subCategories" :key="flyout.id">
            <div v-show="activeIdsMap[flyout.id]">
              <AsyncSubCategories :data="flyout" :hydrate-when="activeIdsMap[flyout.id]" class="transition-panel" @on-select="showSubCategories" />
            </div>
          </template>
        </template>
        <AsyncSubCategories v-else-if="activeCSRCategory" :data="activeCSRCategory" class="transition-panel" @on-select="showSubCategories" />
      </BetterTransitionGroup>
    </div>
  </div>
</template>

<script setup>
/* v8 ignore file -- @preserve */
import { computed, defineAsyncComponent, onUpdated } from 'vue';
import { useMounted } from '@vueuse/core';

import RootCategories from './RootCategories.common';
import SubCategories from './SubCategories.common';
import RegistryInfo from './RegistryInfo.common';
import MobileNavTopBar from './MobileNavTopBar.common';
import BetterTransitionGroup from '~/components/common/BetterTransitionGroup';
import AccountContent from '~/components/account/AccountContent.common';

import { useMobileNav } from '~/composables/useMobileNav';
import { useHamburgerSubCategories } from '~/composables/useHamburgerSubCategories';

import scrollToTop from '~/utils/scrollToTop';
import NavBackButton from './NavBackButton.common';
import { GET_IS_MOBILE_VIEW } from '#shared/types/getters';

const envProps = useEnvProps();
const pageData = usePageData();
const navViewType = useNavViewType();

const { isNavSSR } = envProps;
const mounted = useMounted();
const isDomestic = computed(() => pageData.navigation.context.isDomestic);
const isMobile = computed(() => navViewType[GET_IS_MOBILE_VIEW]);
const AsyncSubCategories = defineAsyncComponent({
  loader: () => Promise.resolve(SubCategories),
  hydrate: () => {},
});
const MobileStoreInfo = defineAsyncComponent(() => import('./MobileStoreInfo'));

const { isRootMode, eventClass, setMode, stack, lastStackItem, isMenuHydrated } = useMobileNav();
const { subCategories, getCategory } = useHamburgerSubCategories();

const activeCSRCategory = computed(() => {
  if (isNavSSR) {
    return null;
  }

  const activeCategory = lastStackItem();

  return activeCategory ? getCategory(activeCategory.id) : null;
});

const activeIdsMap = computed(() => {
  const lastItem = lastStackItem();
  return lastItem ? { [lastItem.id]: true } : {};
});

const flyoutClasses = computed(() => ({
  'l2-flyout': stack.value.length === 1,
  'l3-flyout': stack.value.length === 2,
}));

const activeCategoryData = computed(() => {
  if (stack.value.length) {
    return stack.value[stack.value.length - 1];
  }

  return null;
});

function showSubCategories(params) {
  setMode(params, true);
}

onUpdated(() => {
  scrollToTop('.mobile-nav-wrap');
});

isMenuHydrated.value = true;
</script>

<style lang="scss" scoped>
.body-content {
  padding: 0 16px 36px 20px;
  position: relative;
}

.mobile-nav-content {
  @include breakpoint(large up) {
    .body-content {
      padding: 8px 48px 56px 48px;
    }
  }
}
</style>

<style lang="scss">
.top-content {
  background-color: $gray-5-color;
  height: 71px;

  &--white-bg {
    background-color: $white;

    .button-back {
      background-color: $white;
    }
  }
}

.mobile-nav-content {
  ul {
    gap: 16px;
  }

  @include breakpoint(large up) {
    .category-button:hover {
      span {
        text-decoration: underline;
      }
    }

    .category-link:hover {
      text-decoration: underline;
    }
  }
}
</style>

<style lang="scss" brand="bcom">
.mobile-nav-content {
  @include breakpoint(large up) {
    h5 {
      text-transform: uppercase;
    }
  }
}
</style>
